import styled from '@emotion/styled'
import { LazyLoadingRule, RefreshRule } from '@pubstack/common/src/adm/rules'
import { AdUnit } from '@pubstack/common/src/adunit'
import { FunctionComponent } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { Colors } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import { SelectOptionProp } from '~/components/SelectableOptionsPopover'
import { WithClassName } from '~/types/utils'
import { AdunitDeviceRuleSelector } from './components/AdunitDeviceRuleSelector'
import { LazyLoadingRuleSelector } from './components/LazyLoadingRuleSelector'
import { StackFormData } from './StackEdit'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { AdmFloorPriceSelection } from './components/AdmFloorPriceSelection'
import { Site } from '@pubstack/common/src/site'
import { AdmStack } from '@pubstack/common/src/stack'

const Subtitle = styled.div`
  ${Fonts.P1};
  ${Colors.Jet};
  margin-bottom: 20px;
`

const LazyLoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const AdunitDeviceRulesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const CommitMessageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const CommitSubtitle = styled.div`
  ${Fonts.P1};
  ${Colors.Jet};
`

const CommitTextArea = styled.textarea`
  box-sizing: border-box;
  border-radius: 3px;
  outline: none;
  resize: none;
  border: 1px solid ${Colors.Platinum};
  white-space: pre;
  height: 120px;
  width: 100%;
  background-color: ${Colors.White};
  padding: 10px 12px;
  margin-bottom: 4px;
  :placeholder-shown {
    font-style: italic;
    ${Fonts.P1};
    ${Colors.SlateGrey};
  }
  &:active,
  &:focus {
    border-color: ${Colors.Silver};
  }
`

const Divider = styled.div`
  margin: 20px 0;
  height: 1px;
  max-height: 1px;
  min-height: 1px;
  flex: 1;
  background-color: ${Colors.Platinum};
`

type PureStackRulesStepProps = WithClassName & {
  selectableAdUnits: AdUnit[]
  selectableRefreshRules: RefreshRule[]
  selectableLazyLoadingRules: LazyLoadingRule[]
  selectedAdUnitsIds: string[]
  isEditing: boolean
  isLoading: boolean
  site?: Site
  stack?: AdmStack
}
const UnstyledPureStackRulesStep: FunctionComponent<PureStackRulesStepProps> = ({
  className,
  selectableLazyLoadingRules,
  selectableRefreshRules,
  selectableAdUnits,
  selectedAdUnitsIds,
  isEditing,
  isLoading,
  site,
  stack,
}) => {
  const hasAdmRefresh = useAuthenticatedUser().hasFeature('admRefresh')
  const { control } = useFormContext<StackFormData>()
  const lazyLoadingRules: SelectOptionProp<string>[] = selectableLazyLoadingRules.map((l) => ({ label: l.name, value: l.id, trackingLabel: 'adm-sites-stacks-form-lazy-loading-rule-set' }))
  const lazyLoadingSelectOptions: SelectOptionProp<string>[] = [...lazyLoadingRules, { label: 'No lazy loading', value: 'NO_RULE', trackingLabel: 'adm-sites-stacks-form-no-lazy-loading-rule-set' }]

  // const floorRules: SelectOptionProp<string>[] = selectableFloorRules.map((l) => ({
  //   label: `${l.name} (${displayWithCurrency((l.floor / 100).toFixed(2), currencySymbol)})`,
  //   value: l.id,
  //   trackingLabel: 'adm-sites-stacks-form-floor-rule-set',
  // }))
  // const floorSelectOptions: SelectOptionProp<string>[] = [...floorRules, { label: 'No floor price', value: 'NO_RULE', trackingLabel: 'adm-sites-stacks-form-no-floor-rule-set' }]

  const refreshRule: SelectOptionProp<string>[] = selectableRefreshRules.map((refreshRule) => ({
    label: refreshRule.name,
    value: refreshRule.id,
    trackingLabel: 'adm-sites-stacks-form-refresh-rule-set',
  }))
  const refreshSelectOptions: SelectOptionProp<string>[] = [...refreshRule, { label: 'No refresh', value: 'NO_RULE', trackingLabel: 'adm-sites-stacks-form-no-refresh-rule-set' }]

  const [desktopAdUnits, mobileAdUnits] = useWatch({ control, name: ['configuration.desktopAdUnits', 'configuration.mobileAdUnits'] })
  const availableAdUnits = {
    desktop: selectableAdUnits.filter((adUnit) => desktopAdUnits.map((adUnitConfig) => adUnitConfig.adUnitId).includes(adUnit.id)),
    mobile: selectableAdUnits.filter((adUnit) => mobileAdUnits.map((adUnitConfig) => adUnitConfig.adUnitId).includes(adUnit.id)),
  }

  const selectedAdUnits = selectableAdUnits.filter((adUnit) => selectedAdUnitsIds.includes(adUnit.id))
  const instreamAdUnits = selectedAdUnits.filter((adUnit) => adUnit.isInstream)

  return (
    <div className={className}>
      <Subtitle>Set the rules applied to this stack.</Subtitle>
      <LazyLoadingWrapper>
        <LazyLoadingRuleSelector
          availableDevices={['desktop', 'mobile']}
          availableRules={lazyLoadingSelectOptions}
          description={instreamAdUnits.length ? `Not compatible with instream ad units (${instreamAdUnits.length}/${selectedAdUnits.length})` : ''}
          disabledSelector={instreamAdUnits.length === selectedAdUnits.length}
        />
      </LazyLoadingWrapper>
      <Divider />

      <AdmFloorPriceSelection adUnits={{ desktop: availableAdUnits.desktop, mobile: availableAdUnits.mobile }} isEditing={isEditing} isLoading={isLoading} stack={stack} />

      <Divider />
      {hasAdmRefresh && (
        <>
          <AdunitDeviceRulesWrapper>
            <AdunitDeviceRuleSelector
              fieldRulesName={'refreshRules'}
              title={'Refresh'}
              extraInformation={'Only the ad units with refresh enabled are available below.'}
              availableAdUnits={{ desktop: availableAdUnits.desktop.filter((adUnit) => !adUnit.isInstream), mobile: availableAdUnits.mobile.filter((adUnit) => !adUnit.isInstream) }}
              availableDevices={['desktop', 'mobile']}
              availableRules={refreshSelectOptions}
              description={instreamAdUnits.length ? `Not compatible with instream ad units (${instreamAdUnits.length}/${selectedAdUnits.length})` : ''}
              disabledSelector={instreamAdUnits.length === selectedAdUnits.length}
            />
          </AdunitDeviceRulesWrapper>
          <Divider />
        </>
      )}
      <CommitMessageWrapper>
        <CommitSubtitle>Keep track, describe the stack.</CommitSubtitle>
        <Controller
          control={control}
          name={'commitMessage'}
          render={({ field: { value: commitMessage, onChange } }) => {
            return <CommitTextArea value={commitMessage} onChange={onChange} placeholder={`Just created a stack for ${site?.name}`} />
          }}
        />
      </CommitMessageWrapper>
    </div>
  )
}

export const PureStackRulesStep = styled(UnstyledPureStackRulesStep)``
